import * as React from "react";
import { FieldProps } from "./props";
import api from "../../../api";
import "./autocomplete.scss";
import { Customer } from "../../../core";
import { isObject } from "util";
import { UserAvatar } from "../../render-file/UserAvatar";

const SalesPersonsAutoCompleteInput: React.FunctionComponent<FieldProps> = (
  props
) => {
  const [list, setList] = React.useState<Customer[]>();
  const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
  const [showDropDown, setShowDropDown] = React.useState<boolean>(false);
  const [selectedItem, setSelectedItem] = React.useState<Customer | undefined>(
    props.defaultValue || undefined
  );

  const minAdded: number[] = [];
  const maxAdded: number[] = [];
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isLoaded) {
      loadData("");
    }
    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, []);

  React.useEffect(() => {
    if (!isLoaded) {
      loadData("");
    }
    document.addEventListener("click", handleClickOutside, false);
    return () => {
      document.removeEventListener("click", handleClickOutside, false);
    };
  }, []);

  React.useEffect(() => {
    const id = isObject(props.defaultValue)
      ? props.defaultValue?._id
      : props.defaultValue;
    if (id && id !== selectedItem?._id) {
      setSelectedItem(list?.find((x) => x._id === id));
    }
    if (
      id &&
      id !== selectedItem?._id &&
      !list?.find((x) => x._id === id) &&
      isObject(props.defaultValue)
    ) {
      setSelectedItem(props.defaultValue);
    }
  }, [props.defaultValue]);

  const loadData = async (query: string) => {
    setIsLoaded(true);

    const filter: any = {
      query,
      showroomId: props.showroomId,
      page: 1,
      perPage: 25,
    };

    if (props.hideBlocked) {
      filter.hideBlocked = true;
    }

    let response = await (props.dataLoader?.(query) ??
      api.crud.searchCustomer(props.apiUrl!, filter));
    if (!!props.includeIds?.length) {
      response = response?.filter((x) => !!props.includeIds?.includes(x._id));
    }
    setList(response);
  };

  const clickDropDown = () => {
    if (props.disabled) {
      return;
    }
    loadData("");
    setShowDropDown(!showDropDown);
  };

  const handleChange = (e: { target: any }) => {
    const { value } = e.target;

    loadData(value);
  };

  const handleSelect = (item: Customer) => {
    setList([item]);
    setSelectedItem(item);
    setShowDropDown(false);

    if (props.onChange != null) {
      props.onChange(item);
    }
  };

  const handleClickOutside = (event: { target: any }) => {
    if (
      wrapperRef &&
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target)
    ) {
      setShowDropDown(false);
    }
  };

  const last30DaysAssignedLeadsCount = list?.map(
    (x) => x.last30DaysAssignedLeadsCount || 0
  );

  const last7DaysAssignedLeadsCount = list?.map(
    (x) => x.last7DaysAssignedLeadsCount || 0
  );

  const last24HoursAssignedLeadsCount = list?.map(
    (x) => x.last24HoursAssignedLeadsCount || 0
  );

  const todaysAssignedLeadCount = list?.map(
    (x) => x.todaysAssignedLeadCount || 0
  );

  const minLast30DaysAssignedLeadsCount = !!last30DaysAssignedLeadsCount?.length
    ? Math.min(...last30DaysAssignedLeadsCount)
    : 0;

  const maxLast30DaysAssignedLeadsCount = !!last30DaysAssignedLeadsCount?.length
    ? Math.max(...last30DaysAssignedLeadsCount)
    : 0;

  const minLast7DaysAssignedLeadsCount = !!last7DaysAssignedLeadsCount?.length
    ? Math.min(...last7DaysAssignedLeadsCount)
    : 0;

  const maxLast7DaysAssignedLeadsCount = !!last7DaysAssignedLeadsCount?.length
    ? Math.max(...last7DaysAssignedLeadsCount)
    : 0;

  const minLast24HoursAssignedLeadsCount = !!last24HoursAssignedLeadsCount?.length
    ? Math.min(...last24HoursAssignedLeadsCount)
    : 0;

  const maxLast24HoursAssignedLeadsCount = !!last24HoursAssignedLeadsCount?.length
    ? Math.max(...last24HoursAssignedLeadsCount)
    : 0;

  const minTodaysAssignedLeadCount = !!todaysAssignedLeadCount?.length
    ? Math.min(...todaysAssignedLeadCount)
    : 0;

  const maxTodaysAssignedLeadCount = !!todaysAssignedLeadCount?.length
    ? Math.max(...todaysAssignedLeadCount)
    : 0;

  const getIcon = (min: number, max: number, value: number, item: number) => {
    if (value === 0 && max === min) {
      return <span className="pl-90 ml-1"></span>;
    } else if (max === value) {
      if (maxAdded.includes(item)) {
        return <span className="pl-90 ml-1"></span>;
      }
      maxAdded.push(item);
      return (
        <i className="icon-arrow-left font-xxxs text-green rotate-90deg ml-1"></i>
      );
    } else if (min === value) {
      if (minAdded.includes(item)) {
        return <span className="pl-90 ml-1"></span>;
      }
      minAdded.push(item);
      return (
        <i className="icon-arrow-right font-xxxs text-red rotate-90deg ml-1"></i>
      );
    } else {
      return <span className="pl-90 ml-1"></span>;
    }
  };

  return (
    <React.Fragment>
      {props.label && <label className={props.labelClass}>{props.label}</label>}
      <div className="custom-dropdown position-relative" ref={wrapperRef}>
        <div
          onClick={clickDropDown}
          className="custom-dropdown__value bg-gray-100 border rounded-sm font-normal d-flex align-items-center font-weight-semibold cursor-pointer"
        >
          <div
            className={`avatar-progress avatar-progress--xs position-relative cursor-pointer ${
              selectedItem && props.defaultValue && !props.loadingText
                ? ""
                : "avatar-progress--add"
            }`}
          >
            <svg>
              <circle r="12" cx="50%" cy="50%" className="default"></circle>
              <circle
                r="12"
                cx="50%"
                cy="50%"
                className="progress"
                stroke-dasharray="117"
                stroke-dashoffset="45"
              ></circle>
            </svg>
            {selectedItem && props.defaultValue && !props.loadingText ? (
              <UserAvatar
                id={selectedItem?._id}
                className="position-absolute left-0 right-0 top-0 bottom-0 m-auto rounded-circle"
                alt=""
                title={selectedItem?.name}
                imageUrl={selectedItem.photo?.location}
                image={selectedItem.photo}
              ></UserAvatar>
            ) : (
              <div className="left-0 right-0 top-0 bottom-0 m-auto rounded-circle text-primary font-xs d-inline-flex justify-content-center align-items-center">
                <i className="icon-plus"></i>
              </div>
            )}
          </div>
          <div className="flex-fill text-truncate custom-dropdown__value-text">
            {props.loadingText ? (
              props.loadingText
            ) : (
              <>
                {selectedItem && props.defaultValue
                  ? selectedItem?.knownAs ||
                    selectedItem?.name ||
                    `${selectedItem?.firstName || ""} ${
                      selectedItem?.lastName || ""
                    }`
                  : `Add ${props.placeholder}`}
              </>
            )}
          </div>
          {(!selectedItem || !props.defaultValue || !props.onReset) && (
            <i className="icon-chevron-down ml-auto"></i>
          )}
          {selectedItem && props.defaultValue && props.onReset && (
            <i
              onClick={() => {
                setSelectedItem(undefined);
                props.onReset?.();
              }}
              className="icon-close ml-auto"
            ></i>
          )}
        </div>
        {showDropDown && !props.disabled && (
          <div className="custom-dropdown__menu custom-dropdown__menu--open border overflow-auto w-100 position-absolute left-0 bg-white zIndex-9 pt-0">
            <div className="bg-white py-2 position-sticky top-0 zIndex-1">
              <div className="custom-dropdown__search border bg-gray-100 d-flex">
                <button
                  type="submit"
                  className="bg-transparent border-0 px-2 text-gray-600 d-flex align-items-center"
                >
                  <i className="icon-search"></i>
                </button>
                <input
                  type="text"
                  className="form-control bg-transparent border-0 py-0 px-1 font-weight-semibold font-normal"
                  placeholder={`Search a ${props.placeholder}`}
                  onChange={handleChange}
                  ref={(input) => input && input.focus()}
                />
              </div>
            </div>
            {list?.map((item) => {
              return (
                <div
                  onClick={() => handleSelect(item)}
                  className="custom-dropdown__menu-item border-bottom d-flex align-items-center cursor-pointer"
                >
                  <div className="col gutter-10 pr-0 row sales-person align-items-center">
                    <div className="col-xl-8 col-12 pl-0 d-flex align-items-center">
                      <>
                        <div className="avatar-progress avatar-progress--xs position-relative cursor-pointer">
                          <svg>
                            <circle
                              r="12"
                              cx="50%"
                              cy="50%"
                              className="default"
                            ></circle>
                            <circle
                              r="12"
                              cx="50%"
                              cy="50%"
                              className="progress"
                              stroke-dasharray="117"
                              stroke-dashoffset="75"
                            ></circle>
                          </svg>
                          <UserAvatar
                            id={item?._id}
                            className="position-absolute left-0 right-0 top-0 bottom-0 m-auto rounded-circle"
                            alt=""
                            title={item?.name}
                            imageUrl={item.photo?.location}
                            image={item.photo}
                          ></UserAvatar>
                        </div>
                        {item.name || item.knownAs ? (
                          <div className="truncate d-inline-block font-normal font-weight-semibold">
                            {item.knownAs || item.name}
                          </div>
                        ) : (
                          <div className="truncate d-inline-block font-normal font-weight-semibold">
                            {item?.firstName || ""} {item?.lastName || ""}
                          </div>
                        )}
                      </>
                    </div>
                    <div className="col-xl-4 col-12 d-flex pl-0 pr-0 justify-content-end pt-xl-0 pt-2">
                      <div className="col pl-0 pr-1">
                        <div className="font-xxs">
                          M :{" "}
                          <span className="font-weight-bold">
                            {item.last30DaysAssignedLeadsCount || 0}
                          </span>{" "}
                          {getIcon(
                            minLast30DaysAssignedLeadsCount,
                            maxLast30DaysAssignedLeadsCount,
                            item.last30DaysAssignedLeadsCount || 0,
                            30
                          )}
                        </div>
                      </div>
                      <div className="col pl-0 pr-1">
                        <div className="font-xxs">
                          CRM :{" "}
                          <span className="font-weight-bold">
                            {item.crmAgentAssignedLeadsCount || 0}
                          </span>
                        </div>
                      </div>
                      {/* <div className="col pl-0 pr-2">
                        <div className="font-xxs">
                          All :{' '}
                          <span className="font-weight-bold">
                            {item.allAssignedLeadsCount}
                          </span>
                        </div>
                      </div> */}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default SalesPersonsAutoCompleteInput;
